import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Context for tracking unsaved form state across the application.
 * Used by ChunkErrorBoundary to determine whether to auto-refresh
 * or prompt the operator when a new version is deployed.
 */

type FormDirtyState = Record<string, boolean>;

interface FormDirtyContextValue {
  /** True if any registered form has unsaved changes */
  isDirty: boolean;
  /** Register or update dirty state for a specific form */
  setDirty: (formKey: string, dirty: boolean) => void;
  /** Clear all dirty states (e.g., on successful save) */
  clearAll: () => void;
  /** Get dirty state for a specific form */
  isDirtyForm: (formKey: string) => boolean;
}

const FormDirtyContext = createContext<FormDirtyContextValue>({
  isDirty: false,
  setDirty: () => {},
  clearAll: () => {},
  isDirtyForm: () => false
});

interface FormDirtyProviderProps {
  children: ReactNode;
}

/**
 * Provider that tracks dirty state for all forms in the application.
 * Wrap your app with this to enable smart reload behavior in ChunkErrorBoundary.
 */
export function FormDirtyProvider({ children }: FormDirtyProviderProps) {
  const [dirtyForms, setDirtyForms] = useState<FormDirtyState>({});

  const setDirty = useCallback((formKey: string, dirty: boolean) => {
    setDirtyForms((prev) => {
      // Avoid unnecessary state updates
      if (prev[formKey] === dirty) {
        return prev;
      }
      if (dirty) {
        return { ...prev, [formKey]: true };
      }
      // Remove the key when not dirty to keep state clean
      const { [formKey]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const clearAll = useCallback(() => {
    setDirtyForms({});
  }, []);

  const isDirtyForm = useCallback(
    (formKey: string) => {
      return dirtyForms[formKey] === true;
    },
    [dirtyForms]
  );

  const isDirty = useMemo(() => {
    return Object.values(dirtyForms).some((v) => v === true);
  }, [dirtyForms]);

  const value = useMemo<FormDirtyContextValue>(
    () => ({
      isDirty,
      setDirty,
      clearAll,
      isDirtyForm
    }),
    [isDirty, setDirty, clearAll, isDirtyForm]
  );

  return <FormDirtyContext.Provider value={value}>{children}</FormDirtyContext.Provider>;
}

/**
 * Hook to access form dirty state context.
 * @returns The form dirty context value
 */
export function useFormDirtyContext(): FormDirtyContextValue {
  return useContext(FormDirtyContext);
}

/**
 * Hook for forms to register their dirty state.
 * Automatically cleans up when the form component unmounts.
 *
 * @param formKey - Unique identifier for the form (e.g., 'user-edit-form', 'payment-form')
 * @returns Function to update the dirty state for this form
 *
 * @example
 * \`\`\`tsx
 * function MyForm() {
 *   const setDirty = useFormDirty('my-form');
 *
 *   return (
 *     <Form
 *       onValuesChange={() => setDirty(true)}
 *       onFinish={() => setDirty(false)}
 *     >
 *       ...
 *     </Form>
 *   );
 * }
 * \`\`\`
 */
export function useFormDirty(formKey: string): (dirty: boolean) => void {
  const { setDirty } = useContext(FormDirtyContext);

  // Cleanup on unmount - mark form as not dirty
  useEffect(() => {
    return () => {
      setDirty(formKey, false);
    };
  }, [formKey, setDirty]);

  // Return a stable callback for this specific form
  return useCallback(
    (dirty: boolean) => {
      setDirty(formKey, dirty);
    },
    [formKey, setDirty]
  );
}

// Export context for class components that need direct access
export { FormDirtyContext };
